/**
 * Annuler la session
 */

import { Component, OnInit, Input, ViewChild, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { FormGroup, FormControl, FormBuilder, Validators } from '@angular/forms';
import { Formation } from '../../../../../../../shared/src/lib/model/formation.model';
import { AuthenticationService } from '../../../../../../../shared/src/lib/service/authentification.service';
import { TaskService } from '../../../../../../../shared/src/lib/service/task.service';
import { FormationService } from '../../../../../../../shared/src/lib/service/formation.service';
import { HttpParams } from '@angular/common/http';
import { AssignExerciceComponent } from '../../../../upcoming-classes/assign-exercice/assign-exercice.component';
import { ExerciceAssignedComponent } from '../../../../../../../shared/src/lib/feature/exercice-assigned/exercice-assigned.component';
import { ProgressGaugeComponent } from '../../../../../../../shared/src/lib/feature/progress-gauge/progress-gauge.component';
import { OccurrenceService } from '../../../../../../../shared/src/lib/service/occurrence.service';

@Component({
  selector: 'app-cancel-session',
  templateUrl: './cancel-session.component.html',
  styleUrls: ['./cancel-session.component.scss']
})
export class CancelSessionComponent implements OnInit {
  @Input() pending;
  @Input() formation: Formation; // nécessaire pour le bloc upcoming-assigned
  reasons = [
    { status: 'abs_student_without_justification', keyvalue: 'student_no_show_without_justification' },
    { status: 'abs_teacher_without_justification', keyvalue: 'teacher_no_show' }
  ];
  selected = null;
  // TODO: Typer les formControl
  reasonCtrl = new FormControl<any>('', [Validators.required]);
  detailsCtrl = new FormControl<any>('', [Validators.required, Validators.minLength(3)]);
  noSessionForm: FormGroup;
  reasonChange = false;
  // Champs ajoutés pour le bloc upcoming-assigned
  sheetPermission = false;
  isSheetLanguageAvailable = false;
  assigned_exercises_duration: number = 0;
  should_be_assigned: number = 0;
  minutes_exercices_per_visio: number = 0;
  extraShouldBeAssigned: number = 0; // minutes ajoutées dynamiquement (durée session * minutes_exercices_per_visio)

  // Références vers les composants enfants pour forcer le refresh après fermeture modal
  @ViewChild('onsiteAssigned') onsiteAssigned: ExerciceAssignedComponent;
  @ViewChild('offsiteAssigned') offsiteAssigned: ExerciceAssignedComponent;
  @ViewChild(ProgressGaugeComponent) gaugeComponent: ProgressGaugeComponent;
  /** Incrémenté à la fermeture de la modal pour recharger les listes assignées */
  assignedReloadToken = 0;

  // Gestion modal assign
  showAssignModal = false;
  componentToLoad: any = null;
  componentName: string = '';
  modalParams: any = {};

  constructor(
    private router: Router,
    private occurrenceService: OccurrenceService,
    private builder: FormBuilder,
    private authenticationService: AuthenticationService,
    private taskService: TaskService,
    private formationService: FormationService,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.noSessionForm = this.builder.group({
      reason: this.reasonCtrl,
      details: this.detailsCtrl
    });

    this.reasonCtrl = new FormControl(this.pending.status);
    this.detailsCtrl = new FormControl(this.pending.cancelled_message);
    this.subcribeToFormChanges();

    // Initialisation permissions / langues si formation disponible
    if (this.formation) {
      this.checkSheetsPermission();
      this.checkAvailableLanguageSheets();
      this.getAssignedExercisesDuration();
      this.loadExerciseInfoAndComputeExtra();
    }
  }

  subcribeToFormChanges() {
    this.reasonCtrl.valueChanges.subscribe(value => {
      this.reasonChange = true;
    });
  }

  // Méthodes inspirées de ValidSessionComponent (simplifiées)
  checkAvailableLanguageSheets() {
    if (this.formation) {
      this.isSheetLanguageAvailable = this.authenticationService.checkAvailableLanguageSheets(this.formation.language_detail.id);
    }
  }

  checkSheetsPermission() {
    this.sheetPermission = this.authenticationService.checkFeaturePermission('sheets');
  }

  getAssignedExercisesDuration() {
    if (!this.formation || this.formation.percent_exercice_rate === 0) {
      this.assigned_exercises_duration = 0;
      this.should_be_assigned = 0;
      return;
    }
    this.formationService.getFormationExercicesInfo(this.formation.id).subscribe({
      next: (exerciceInfos) => {
        this.assigned_exercises_duration = exerciceInfos.total_assigned.duration;
        this.should_be_assigned = exerciceInfos.should_be_assigned ? Math.ceil(exerciceInfos.should_be_assigned) : 0;
        // minutes_exercices_per_visio déjà récupéré ailleurs mais on peut sync
        this.minutes_exercices_per_visio = exerciceInfos.minutes_exercices_per_visio || this.minutes_exercices_per_visio;
      }
    });
  }

  private loadExerciseInfoAndComputeExtra() {
    if (!this.formation || this.formation.percent_exercice_rate === 0) {
      this.extraShouldBeAssigned = 0;
      return;
    }
    this.formationService.getFormationExercicesInfo(this.formation.id).subscribe({
      next: (exerciceInfos) => {
        this.minutes_exercices_per_visio = exerciceInfos.minutes_exercices_per_visio || 0;
        this.computeExtraShouldBeAssigned();
      }
    });
  }

  private computeExtraShouldBeAssigned() {
    if (!this.pending) {
      this.extraShouldBeAssigned = 0;
      return;
    }
    const start = this.pending.start ? new Date(this.pending.start) : null;
    const end = this.pending.end ? new Date(this.pending.end) : null;
    if (!start || !end) {
      this.extraShouldBeAssigned = 0;
      return;
    }
    const durationMinutes = (end.getTime() - start.getTime()) / 60000; // minutes
    this.extraShouldBeAssigned = Math.round(durationMinutes * this.minutes_exercices_per_visio);
  }

  checkExerciceRate(): boolean {
    // Même logique que valid-session mais pourrait intégrer extraShouldBeAssigned si nécessaire.
    if (!this.formation) return true; // sécurité
    if (this.formation.percent_exercice_rate === 0) {
      return true;
    }
    const required = this.should_be_assigned + this.extraShouldBeAssigned;
    if (this.formation.percent_exercice_rate > 0 && this.assigned_exercises_duration >= required) {
      return true;
    }
    return false;
  }

  reloadExerciceDuration() {
    this.getAssignedExercisesDuration();
    if (this.gaugeComponent) {
      this.gaugeComponent.getAssignedExercisesDuration();
    }
  }

  // Ouvre la modal d'assignation
  openAssign(type: 'onsite' | 'offsite', category?: string) {
    this.componentToLoad = AssignExerciceComponent;
    this.componentName = 'AssignExerciceComponent';
    this.modalParams = {
      formationId: this.formation?.id,
      typeExercice: type,
      typeCategory: category,
      occurrenceId: this.pending?.id,
      sessionUrl: this.pending?.session?.url,
      openInModal: true
    };
    this.showAssignModal = true;
    this.changeDetectorRef.detectChanges();
  }

  closeAssignModal() {
    this.showAssignModal = false;
    this.refreshAfterModal();
  }

  handleModalEvent(event: any) {
    // Fermeture standard
    this.closeAssignModal();
  }

  private refreshAfterModal() {
    this.getAssignedExercisesDuration();
    // Refresh gauge
    if (this.gaugeComponent) {
      this.gaugeComponent.getAssignedExercisesDuration();
    }
    // Forcer le rechargement des listes (ViewChild.reload est peu fiable avec @if)
    this.assignedReloadToken++;
    this.onsiteAssigned?.reload();
    this.offsiteAssigned?.reload();
  }

  sendReport() {
    this.occurrenceService.patchOccurrence(this.pending.url, { status: this.reasonCtrl.value.status, cancelled_message: this.detailsCtrl.value })
      .then(_ => {
        this.router.navigate(['/reports']);
      })
  }

}
